import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../components/ui/Icon';
import PaginationControls from '../components/ui/PaginationControls';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import DiagnosticWizard from '../components/DiagnosticWizard';
import './Diagnostic.css';
import { getUser } from '../utils/authUtil';

const PAGE_SIZE = 20;

const DiagnosticAnalysis = () => {
  const navigate = useNavigate();
  // Server-side pagination (audit SA-08): the database filters, sorts and slices; this page only ever holds one page
  // of rows plus the whole-scope summary and the filter options the API returns.
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState({ branches: [], batch_years: [], readiness_levels: [], colleges: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [batch, setBatch] = useState('all');
  const [branch, setBranch] = useState('all');
  const [readiness, setReadiness] = useState('all');
  const [college, setCollege] = useState('all');
  const [page, setPage] = useState(1);
  const [reloadTick, setReloadTick] = useState(0);
  const [activeTab, setActiveTab] = useState('reports');
  const [showWizard, setShowWizard] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState(null);

  const [createForm, setCreateForm] = useState({
    test_name: '',
    duration_minutes: 30,
    total_marks: 10
  });
  const [createMsg, setCreateMsg] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const user = getUser();
  const isSuperAdmin = user?.realRole === 'super_admin';

  useEffect(() => { const h = setTimeout(() => setDebouncedSearch(search.trim()), 300); return () => clearTimeout(h); }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch, batch, branch, readiness, college]);

  useEffect(() => {
    if (activeTab !== 'reports') return;
    let cancelled = false;
    setLoading(true);
    const params = { page, limit: PAGE_SIZE };
    if (debouncedSearch) params.search = debouncedSearch;
    if (branch !== 'all') params.branch = branch;
    if (batch !== 'all') params.batch_year = batch;
    if (readiness !== 'all') params.readiness = readiness;
    if (college !== 'all') params.college_id = college;
    API.get('/diagnostic/reports', { params })
      .then(res => {
        if (cancelled) return;
        setRows(res.data.data || []); setMeta(res.data.metadata || { total: 0, total_pages: 1 });
        setStats(res.data.stats || null);
        setOptions(res.data.filters || { branches: [], batch_years: [], readiness_levels: [], colleges: [] });
      })
      .catch(err => console.error(err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeTab, page, debouncedSearch, branch, batch, readiness, college, reloadTick]);

  const fetchStudents = () => setReloadTick(n => n + 1);

  const handleCreateDiagnostic = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateMsg('');

    try {
      const res = await API.post('/tests', {
        test_name: createForm.test_name,
        test_type: 'Diagnostic Test',
        duration_minutes: parseInt(createForm.duration_minutes) || 30,
        total_marks: parseInt(createForm.total_marks) || 10,
        negative_marking: false
      });

      setCreateMsg(`Success! Initializing wizard...`);
      setSelectedTestId(res.data.test.id);
      setShowWizard(true);
      setCreateForm({ test_name: '', duration_minutes: 30, total_marks: 10 });
    } catch (err) {
      setCreateMsg(`Error: ${err.response?.data?.message || 'Failed to create test'}`);
    } finally {
      setCreateLoading(false);
    }
  };

  const batches = options.batch_years, branches = options.branches, readinessLevels = options.readiness_levels, colleges = options.colleges;

  // Super admin: group the current page's rows by college for a separated view. With pagination this groups only what
  // the page currently holds (not the whole platform) — consistent with the rest of the admin pagination pattern.
  const groupedByCollege = useMemo(() => {
    const groups = new Map();
    rows.forEach(s => {
      const key = s.college_name || 'Unassigned College';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(s);
    });
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  // Presentational-only: map a readiness_level string to a badge colour variant.
  const readinessVariant = (level) => {
    const l = (level || '').toLowerCase();
    if (l.includes('top')) return 'top';
    if (l.includes('ready')) return 'ready';
    if (l.includes('moderate')) return 'moderate';
    if (l.includes('need')) return 'needs';
    return 'neutral';
  };

  const renderStudentCard = (s) => {
    const dateObj = new Date(s.completed_at);
    const formattedDate = !isNaN(dateObj) ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase() : (s.completed_at ? s.completed_at : 'OCT 24, 2023');
    const badgeText = s.last_test_title ? s.last_test_title.substring(0, 8) : 'TEST A';

    return (
      <div key={s.id} className="diag-student-card" onClick={() => navigate(`/admin/diagnostic/report/${s.attempt_id}`)}>
        <div className="dsc-avatar">{s.name ? s.name.charAt(0) : '?'}</div>

        <div className="dsc-content-wrapper">
          <h3 className="dsc-name">{s.name}</h3>
          <div className="dsc-content-mid">
            <span className="dsc-icon"><Icon name="profile" size={22} /></span>
            <span className="dsc-reg">{s.roll_number} | {s.branch}</span>
          </div>
          <div className="dsc-content-bottom">
            <span className="dsc-test-badge">{badgeText}</span>
            <span className="dsc-date">{formattedDate}</span>
          </div>
        </div>

        <div className="dsc-metrics">
          {s.readiness_level ? (
            <span className={`dsc-status-badge ${readinessVariant(s.readiness_level)}`}>
              {s.readiness_level}{s.provisional ? ' (provisional)' : ''}
            </span>
          ) : (
            <span className="dsc-status-badge">Insufficient evidence</span>
          )}
          {s.eri_score != null && (
            <div className="dsc-eri">
              <span className="dsc-eri-label">ERI</span>
              <span className="dsc-eri-value">{parseFloat(s.eri_score).toFixed(0)}</span>
            </div>
          )}
        </div>

        <button type="button" className="dsc-action" tabIndex={-1}>
          View Report
          <span className="dsc-action-arrow"><Icon name="arrow-right" size={18} /></span>
        </button>
      </div>
    );
  };

  if (showWizard && selectedTestId) {
    return (
      <div className="diagnostic-wizard-modal">
        <DiagnosticWizard
          testId={selectedTestId}
          onComplete={() => { setShowWizard(false); fetchStudents(); setActiveTab('reports'); }}
          onCancel={() => { setShowWizard(false); setSelectedTestId(null); }}
        />
      </div>
    );
  }

  return (
    <div className="diagnostic-page-content">
      <div className="diagnostic-page-wrapper">
        <header className="bento-header diag-mgmt-toolbar">
          <div className="b-meta">
            <h1 className="premium-title">Diagnostic Analysis</h1>
            <p className="premium-subtitle">
              {isSuperAdmin ? 'Centralized cognitive diagnostic management' : 'Placement readiness & skill analysis dashboard'}
            </p>
          </div>
          <div className="diag-toolbar-actions">
            {activeTab === 'reports' && (
              <div className="diag-search-wrap">
                <svg className="diag-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search students, roll numbers, or colleges..."
                  className="diag-search-input"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            )}
            {isSuperAdmin && activeTab === 'reports' && (
              <button className="btn-submit-premium" style={{ width: 'auto', padding: '12px 24px' }} onClick={() => setActiveTab('create')}>
                <span>+</span> Create New Scan
              </button>
            )}
          </div>
        </header>

        <nav className="tabs-container">
          <button className={`tab-link ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>
            Student Reports
          </button>
          {isSuperAdmin && (
            <button className={`tab-link ${activeTab === 'create' ? 'active' : ''}`} onClick={() => setActiveTab('create')}>
              Assessment Engine
            </button>
          )}
        </nav>

        <main className="tab-content">
          {activeTab === 'reports' ? (
            <div className="reports-view">
              {!loading && stats && stats.total > 0 && (
                <div className="diag-filters">
                  <select className="diag-filter-select" value={batch} onChange={e => setBatch(e.target.value)}>
                    <option value="all">All Batches</option>
                    {batches.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <select className="diag-filter-select" value={branch} onChange={e => setBranch(e.target.value)}>
                    <option value="all">All Branches</option>
                    {branches.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <select className="diag-filter-select" value={readiness} onChange={e => setReadiness(e.target.value)}>
                    <option value="all">All Readiness</option>
                    {readinessLevels.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {isSuperAdmin && (
                    <select className="diag-filter-select" value={college} onChange={e => setCollege(e.target.value)}>
                      <option value="all">All Colleges</option>
                      {colleges.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                  <span className="diag-filter-count">{meta.total} of {stats.total} student{stats.total !== 1 ? 's' : ''}</span>
                </div>
              )}

              {loading ? (
                <div className="loading-state">Fetching student performance records…</div>
              ) : rows.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon"><Icon name="chart" size={40} /></div>
                  <h3>No diagnostic records</h3>
                  <p>Assign a diagnostic test to see real-time skill mapping.</p>
                </div>
              ) : isSuperAdmin ? (
                <div className="diag-college-groups">
                  {groupedByCollege.map(([collegeName, list]) => (
                    <section key={collegeName} className="diag-college-group">
                      <div className="diag-college-header">
                        <span className="diag-college-name"><Icon name="building" size={14} /> {collegeName}</span>
                        <span className="diag-college-count">{list.length} student{list.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="diag-student-grid">
                        {list.map(renderStudentCard)}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div className="diag-student-grid">
                  {rows.map(renderStudentCard)}
                </div>
              )}
              {!loading && rows.length > 0 && (
                <PaginationControls page={page} totalPages={meta.total_pages} total={meta.total} onChange={setPage} />
              )}
            </div>
          ) : (
            <div className="create-section-wrapper">
              <div className="create-card-premium">
                <div className="create-card-header">
                  <h3>Configure Diagnostic Scan</h3>
                  <p>Setup a new automated diagnostic test for student mapping.</p>
                </div>
                <form onSubmit={handleCreateDiagnostic} className="premium-form">
                  <div className="form-group-p">
                    <label>Assesment Title</label>
                    <input type="text" value={createForm.test_name}
                      onChange={e => setCreateForm({ ...createForm, test_name: e.target.value })}
                      placeholder="e.g., Engineering Aptitude Level II" required />
                  </div>
                  <div className="form-row-p">
                    <div className="form-group-p">
                      <label>Duration (mins)</label>
                      <input type="number" value={createForm.duration_minutes}
                        onChange={e => setCreateForm({ ...createForm, duration_minutes: e.target.value })} />
                    </div>
                    <div className="form-group-p">
                      <label>Target Qs</label>
                      <input type="number" value={createForm.total_marks}
                        onChange={e => setCreateForm({ ...createForm, total_marks: e.target.value })} />
                    </div>
                  </div>
                  <button type="submit" disabled={createLoading} className="btn-submit-premium">
                    {createLoading ? 'Initializing Scan…' : 'Create & Assign'}
                  </button>
                  {createMsg && <div className={`msg-box-p ${createMsg.startsWith('Error:') ? 'error' : 'success'}`}>{createMsg}</div>}
                </form>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default DiagnosticAnalysis;
